/**
 * AppLayout — Parent portal layout
 *
 * Warm light theme with glassmorphism nav surfaces.
 * Desktop: Slim icon-only sidebar (64px)
 * Mobile: Bottom nav + slide-out sidebar
 */

import React, { useState, useMemo } from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useSelectedChild } from '@/contexts/ChildContext';
import { useParentStatus } from '@/hooks/useParentStatus';
import { useTranslation } from '@/lib/i18n';
import {
  Calendar,
  BookOpen,
  LogOut,
  Menu,
  ChevronLeft,
  User,
} from 'lucide-react';
import { CardIcon } from '@/components/icons/CardIcon';
import { LanguageSelector } from '@/components/shared/LanguageSelector';
import { glassStyles, colors } from '@/lib/design';

interface AppLayoutProps {
  children: React.ReactNode;
  hideBottomNav?: boolean;
}

const navItems = [
  { path: '/schedule', label: 'Schedule', icon: Calendar },
  { path: '/consonant-practice', label: 'Cards', icon: CardIcon },
  { path: '/read', label: 'Pages', icon: BookOpen },
];

const mobileNavItems = [
  { path: '/', icon: null, label: 'Home' },
  { path: '/schedule', label: 'Schedule', icon: Calendar },
  { path: '/consonant-practice', label: 'Cards', icon: CardIcon },
  { path: '/read', label: 'Pages', icon: BookOpen },
];

interface NavIconButtonProps {
  path: string;
  label: string;
  icon: React.FC<{ className?: string }>;
  isActive: boolean;
  onClick?: () => void;
}

const NavIconButton: React.FC<NavIconButtonProps> = ({
  path,
  label,
  icon: Icon,
  isActive,
  onClick,
}) => (
  <Link
    to={path}
    onClick={onClick}
    className={`
      group relative w-12 h-12 rounded-xl flex items-center justify-center transition-all
      ${isActive
        ? 'bg-[#5C2D91]/12 text-[#5C2D91]'
        : 'text-[#8E8E93] hover:bg-black/[0.04] hover:text-[#1C1C1E]'
      }
    `}
    title={label}
  >
    <Icon className={`w-6 h-6 ${isActive ? 'stroke-[2]' : ''}`} />
    <div className="absolute left-full ml-3 px-2 py-1 bg-[#1C1C1E] text-white text-xs font-medium rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50 pointer-events-none">
      {label}
    </div>
    {isActive && (
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-[#5C2D91] rounded-r-full" />
    )}
  </Link>
);

export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  hideBottomNav = false,
}) => {
  const { user, profile, isParent, isAdmin, loading, signOut } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { selectedChildId } = useSelectedChild();
  const parentStatus = useParentStatus({ childId: selectedChildId ?? undefined });

  const filteredNavItems = useMemo(() =>
    navItems.filter(item => {
      if (item.path === '/consonant-practice') return parentStatus.canAccessConsonant;
      if (item.path === '/read') return parentStatus.canAccessLearn;
      return true;
    }), [parentStatus.canAccessConsonant, parentStatus.canAccessLearn]
  );

  const filteredMobileNavItems = useMemo(() =>
    mobileNavItems.filter(item => {
      if (item.path === '/consonant-practice') return parentStatus.canAccessConsonant;
      if (item.path === '/read') return parentStatus.canAccessLearn;
      return true;
    }), [parentStatus.canAccessConsonant, parentStatus.canAccessLearn]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#5C2D91]/20 border-t-[#5C2D91]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const handleSignOut = async () => {
    await signOut();
  };

  const isAdminPreview = isAdmin() && !isParent();

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#FAF6F0]">
      {/* Admin Preview Banner */}
      {isAdminPreview && (
        <div
          className="py-2 px-4 text-center text-sm fixed top-0 left-0 right-0 z-[60]"
          style={{
            background: `linear-gradient(90deg, ${colors.accent.orange}, ${colors.accent.yellow})`,
            color: '#000000',
          }}
        >
          <span className="font-semibold">{t('Admin Preview Mode')}</span>
          <span className="mx-2 opacity-60">&bull;</span>
          <Link to="/admin" className="underline hover:no-underline font-medium">
            {t('Return to Admin')}
          </Link>
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          style={glassStyles.overlay}
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-72 transform transition-transform duration-300 ease-out
          ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:hidden
          ${isAdminPreview ? 'pt-10' : ''}
        `}
        style={glassStyles.sidebar}
      >
        <div className="flex items-center justify-between p-5 border-b border-black/[0.06]">
          <Link to="/" className="flex items-center gap-3" onClick={() => setMobileSidebarOpen(false)}>
            <img src="/turn-2.png" alt="Turn" className="h-8 w-auto object-contain" />
            <span className="text-lg font-semibold text-[#1C1C1E]">Turn</span>
          </Link>
          <button
            onClick={() => setMobileSidebarOpen(false)}
            className="p-2 text-[#8E8E93] hover:text-[#1C1C1E] hover:bg-black/[0.04] rounded-xl transition-all"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-4 space-y-1">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                  ${active
                    ? 'bg-[#5C2D91]/8 text-[#5C2D91] font-medium'
                    : 'text-[#636366] hover:bg-black/[0.04] hover:text-[#1C1C1E]'
                  }
                `}
              >
                <Icon className={`w-5 h-5 ${active ? 'text-[#5C2D91]' : ''}`} />
                {t(item.label)}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-black/[0.06]">
          <Link
            to="/settings"
            onClick={() => setMobileSidebarOpen(false)}
            className="w-full flex items-center gap-3 px-4 py-3 mb-2 text-[#636366] hover:bg-black/[0.04] hover:text-[#1C1C1E] rounded-xl transition-all"
          >
            <User className="w-5 h-5" />
            <span className="font-medium">{profile?.name || t('Settings')}</span>
          </Link>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-4 py-3 text-[#8E8E93] hover:text-[#FF3B30] hover:bg-[#FF3B30]/5 rounded-xl transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">{t('Sign Out')}</span>
          </button>
        </div>
      </aside>

      {/* Desktop Slim Sidebar */}
      {!hideBottomNav && (
        <aside
          className={`hidden md:flex flex-col fixed top-0 left-0 h-full w-16 z-50 ${isAdminPreview ? 'pt-10' : ''}`}
          style={glassStyles.sidebar}
        >
          <div className="flex items-center justify-center py-4 border-b border-black/[0.06]">
            <Link to="/" className="w-10 h-10 flex items-center justify-center transition-all hover:scale-105" title={t('Turn Home')}>
              <img src="/turn-2.png" alt="Turn" className="w-8 h-8 object-contain" />
            </Link>
          </div>

          <nav className="flex-1 flex flex-col items-center gap-2 py-4 px-2">
            {filteredNavItems.map((item) => (
              <NavIconButton
                key={item.path}
                path={item.path}
                label={t(item.label)}
                icon={item.icon}
                isActive={isActive(item.path)}
              />
            ))}
          </nav>

          <div className="flex flex-col items-center gap-2 py-4 px-2 border-t border-black/[0.06]">
            <Link
              to="/settings"
              className={`
                group relative w-12 h-12 rounded-xl flex items-center justify-center transition-all
                ${isActive('/settings')
                  ? 'bg-[#5C2D91]/12 text-[#5C2D91]'
                  : 'text-[#8E8E93] hover:bg-black/[0.04] hover:text-[#1C1C1E]'
                }
              `}
              title={t('Settings')}
            >
              <div className="w-8 h-8 bg-[#5C2D91]/10 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-[#5C2D91]">
                  {profile?.name?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="absolute left-full ml-3 px-2 py-1 bg-[#1C1C1E] text-white text-xs font-medium rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50 pointer-events-none">
                {t('Settings')}
              </div>
            </Link>

            <button
              onClick={handleSignOut}
              className="group relative w-12 h-12 rounded-xl flex items-center justify-center text-[#8E8E93] hover:text-[#FF3B30] hover:bg-[#FF3B30]/5 transition-all"
              title={t('Sign Out')}
            >
              <LogOut className="w-5 h-5" />
              <div className="absolute left-full ml-3 px-2 py-1 bg-[#1C1C1E] text-white text-xs font-medium rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50 pointer-events-none">
                {t('Sign Out')}
              </div>
            </button>
          </div>
        </aside>
      )}

      {/* Main Content */}
      <div className={`${hideBottomNav ? '' : 'md:ml-16'} ${isAdminPreview ? 'pt-10' : ''}`}>
        <header className="sticky top-0 z-20" style={glassStyles.header}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14">
              <div className="flex items-center gap-3">
                {!hideBottomNav && (
                  <button
                    onClick={() => setMobileSidebarOpen(true)}
                    className="md:hidden p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#8E8E93] hover:text-[#1C1C1E] hover:bg-black/[0.04] rounded-xl transition-all"
                  >
                    <Menu className="w-6 h-6" />
                  </button>
                )}
                <Link to="/" className="flex items-center gap-2 md:hidden">
                  <img src="/turn-2.png" alt="Turn" className="w-8 h-8 rounded-lg" />
                  <span className="text-lg font-semibold text-[#1C1C1E]">Turn</span>
                </Link>
              </div>

              {hideBottomNav && (
                <Link to="/" className="hidden md:flex items-center gap-2">
                  <img src="/turn-2.png" alt="Turn" className="w-8 h-8 rounded-lg" />
                  <span className="text-lg font-semibold text-[#1C1C1E]">Turn</span>
                </Link>
              )}

              <div className="flex items-center gap-2">
                <LanguageSelector />
                {hideBottomNav && (
                  <button
                    onClick={handleSignOut}
                    className="p-2 text-[#8E8E93] hover:text-[#FF3B30] rounded-xl transition-all hover:bg-[#FF3B30]/5 md:hidden"
                    title={t('Sign Out')}
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className={hideBottomNav ? '' : 'pb-20 md:pb-8'}>
          {children}
        </main>

        {/* Mobile Bottom Navigation */}
        {!hideBottomNav && (
          <nav
            className="md:hidden fixed bottom-0 left-0 right-0 z-30 safe-area-pb glass-nav"
            style={{ borderTop: '1px solid rgba(0, 0, 0, 0.06)' }}
          >
            <div className="flex justify-around py-2">
              {filteredMobileNavItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`
                      flex flex-col items-center gap-1 px-4 py-2.5 rounded-xl transition-all
                      ${active
                        ? 'text-[#5C2D91]'
                        : 'text-[#8E8E93] hover:text-[#636366]'
                      }
                    `}
                  >
                    {item.path === '/' ? (
                      <img src="/turn-2.png" alt="Turn" className="w-5 h-5" />
                    ) : (
                      Icon && <Icon className={`w-6 h-6 ${active ? 'stroke-[2.5]' : ''}`} />
                    )}
                    <span className={`text-xs ${active ? 'font-semibold' : ''}`}>
                      {t(item.label)}
                    </span>
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </div>
    </div>
  );
};

export default AppLayout;
